import {
  colorBackgroundAltInverse,
  colorBaseNeutralWhite,
} from '@duxton-web/design-tokens/themes/light';
import {
  ChevronDownUniversal,
  ChevronTopUniversal,
  LaunchUniversal,
} from '@duxton-web/icons';
import {
  Alert,
  Button,
  Collapse,
  Flex,
  Form,
  Input,
  Select,
  Typography,
} from '@duxton-web/ui';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import SmartCampaignCallToAction from 'omega-rtc/components/form/ucd/SmartCampaignCallToActions';
import {
  OBJECTIVES_ICON_LABEL,
  SMART_CAMPAIGN_FORM_KEYS,
  TONE_OPTIONS,
} from 'omega-rtc/constants/ucd';
import { useCreateSmartCampaign } from 'omega-rtc/components/container/CreateSmartCampaignProvider';

const SmartCampaignMetaDataImplicitInputs = ({
  productOverview,
  reasonToUse,
  callToAction,
  vertical,
  projectDetails,
  tone,
  isCVPOthers,
  callToActionOptions,
  handleAddNewItemInCallToAction,
}) => {
  const { showEditableFormInputs, setShowEditableFormInputs } =
    useCreateSmartCampaign();
  const expandIconOfCollapse = ({ isActive }) => (
    <Button
      type="text"
      onClick={(e) => e.preventDefault()}
      style={{
        width: 32,
        height: 32,
        backgroundColor: colorBaseNeutralWhite,
      }}
      icon={isActive ? <ChevronDownUniversal /> : <ChevronTopUniversal />}
    />
  );

  const showEditOption = () =>
    !isCVPOthers && vertical && projectDetails && projectDetails.project;

  return (
    <Collapse
      ghost
      style={{
        width: '100%',
        padding: '0px 16px',
      }}
      expandIconPosition="end"
      expandIcon={expandIconOfCollapse}
      collapsible="icon"
      items={[
        {
          label: (
            <Flex justify="space-between" align="center">
              <Typography.Body strong level={1}>
                Implicit Input
              </Typography.Body>
              {showEditOption() && !showEditableFormInputs && (
                <Button
                  style={{ marginRight: 8 }}
                  size="small"
                  onClick={() =>
                    setShowEditableFormInputs(!showEditableFormInputs)
                  }
                >
                  Edit
                  <LaunchUniversal />
                </Button>
              )}
            </Flex>
          ),
          children: (
            <Flex gap={24} vertical style={{ padding: '8px 24px 24px 8px' }}>
              <ImplicitInputs
                vertical={vertical}
                projectView={projectDetails}
                reasonToUse={reasonToUse}
                tone={tone}
                callToAction={callToAction}
                productOverview={productOverview}
                isEditableInputs={showEditableFormInputs}
                isCVPOthers={isCVPOthers}
                callToActionOptions={callToActionOptions}
                handleAddNewItemInCallToAction={handleAddNewItemInCallToAction}
              />
              <Flex>
                {/* Button is disable since, we don't have link to feedback. */}
                <Button size="middle" onClick={() => {}} disabled>
                  Give feedback
                  <LaunchUniversal />
                </Button>
              </Flex>
            </Flex>
          ),
        },
      ]}
    />
  );
};

const ImplicitInputs = ({
  vertical,
  projectView,
  reasonToUse,
  callToAction,
  productOverview,
  tone,
  isEditableInputs,
  isCVPOthers,
  callToActionOptions,
  handleAddNewItemInCallToAction,
}) => {
  if (!projectView || !vertical) {
    return (
      <Typography.Body level={2} style={{ color: colorBackgroundAltInverse }}>
        After selecting the options above to set up the campaign, we will show
        the implicit input here.
      </Typography.Body>
    );
  }

  const countries = useSelector((state) => state.countries.entities);

  const projectDetails = projectView.project;
  const objectiveLabel = useMemo(() => {
    const projectObjective = projectDetails && projectDetails.objective;

    if (!projectObjective) {
      return;
    }

    return OBJECTIVES_ICON_LABEL[projectObjective].label;
  }, [projectDetails]);

  const countryLabel = useMemo(() => {
    const countryId = projectDetails && projectDetails.countryID;

    if (!countries || !countryId) {
      return;
    }

    return countries.find((country) => country.countryID === Number(countryId))
      .name;
  }, [projectDetails]);

  return (
    <React.Fragment>
      <Alert
        closable
        showIcon
        type="info"
        style={{ padding: 16, gap: 8 }}
        message="What is implicit input?"
        description="Based on the options you selected above, we will automatically reuse existing input to help prompt more efficiently. If you want to replace any of the following inputs, please use the campaign detail field above by “replace XYZ with...”"
      />
      {!isEditableInputs && (
        <Flex gap={24} vertical style={{ padding: '0px 24px 0px 8px' }}>
          <ItemContainer title={'Country'} content={countryLabel} />
          <ItemContainer title={'Objective'} content={objectiveLabel} />
          <ItemContainer title={'Product Overview'} content={productOverview} />
          <ItemContainer title={'Reason to Use'} content={reasonToUse} />
          <ItemContainer
            title={'Common Call to Action'}
            content={getCallToActionsInListView({ callToAction })}
          />
          <ItemContainer title={'Tone'} content={tone} />
        </Flex>
      )}
      {isEditableInputs && (
        <div>
          <Form.Item
            required
            label="Product Overview"
            hidden={isCVPOthers && !isEditableInputs}
            name={SMART_CAMPAIGN_FORM_KEYS.PRODUCT_OVERVIEW}
            rules={[
              { message: 'Please input product overview', required: true },
            ]}
          >
            <Input.TextArea
              placeholder="Enter product overview"
              autoSize={{ minRows: 5, maxRows: 10 }}
            />
          </Form.Item>
          <Form.Item
            required
            label="Reason to Use"
            hidden={isCVPOthers && !isEditableInputs}
            name={SMART_CAMPAIGN_FORM_KEYS.REASON_TO_USE}
            rules={[{ message: 'Please input reason to use', required: true }]}
          >
            <Input.TextArea
              placeholder="Enter reason to use"
              autoSize={{ minRows: 5, maxRows: 10 }}
            />
          </Form.Item>
          <SmartCampaignCallToAction
            callToActionOptions={callToActionOptions}
            handleAddNewItemInCallToAction={handleAddNewItemInCallToAction}
            isHidden={isCVPOthers && !isEditableInputs}
          />
          <Form.Item
            label="Tone"
            name={SMART_CAMPAIGN_FORM_KEYS.TONE}
            hidden={isCVPOthers && !isEditableInputs}
            rules={[{ message: 'Please select a tone value', required: true }]}
          >
            <Select options={TONE_OPTIONS} />
          </Form.Item>
        </div>
      )}
    </React.Fragment>
  );
};

const getCallToActionsInListView = ({ callToAction: callToActions }) => {
  if (!callToActions) {
    return;
  }

  return (
    <ul style={{ listStyle: 'disc' }}>
      {callToActions.map((callToAction) => (
        <li key={callToAction}>{callToAction}</li>
      ))}
    </ul>
  );
};

const ItemContainer = ({ title, content }) => {
  if (!content) {
    return <React.Fragment />;
  }
  return (
    <Flex vertical gap={4}>
      <Typography.Body level={2} style={{ color: colorBackgroundAltInverse }}>
        {title}
      </Typography.Body>
      <Typography.Body level={2}>{content || '-'}</Typography.Body>
    </Flex>
  );
};
export default SmartCampaignMetaDataImplicitInputs;
